import { AlertTriangle, CheckCircle, Truck } from 'lucide-react';
import React from 'react';

export const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);
export const fmtCur = (n) => `₹${fmt(n)}`;
export const fmtWt = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '0';
  if (v < 0.01)  return v.toFixed(4);
  if (v < 0.1)   return v.toFixed(4);
  if (v < 1)     return v.toFixed(3);
  return fmt(v);
};

export const EMPTY_PART = {
  part_number: '', part_name: '', customer_name: '', material_category: '', material_grade: '',
  thickness_mm: '',
  net_weight: '', gross_weight: '', scrap_percentage: '', tooling_cost: '', tooling_life: 10000,
  overhead_percentage: 15, selling_price: '',
  rm_price_per_kg: '', scrap_price_per_kg: '',
  plating_cost: '',
  processes: [{ name: '', cost: '' }],
  bop_items: [{ name: '', cost: '' }],
};

export function RmVarianceBadge({ variance }) {
  if (!variance) return <span className="text-xs text-slate-300">—</span>;
  const { locked_price, market_price, diff_per_kg, status } = variance;
  if (status === 'behind') {
    return (
      <div className="text-right" data-testid="rm-status-behind">
        <div className="flex items-center justify-end gap-1.5">
          <AlertTriangle size={12} className="text-red-500" />
          <span className="text-xs font-bold text-red-600">BEHIND</span>
        </div>
        <p className="text-[10px] font-mono text-red-500 mt-0.5">
          Locked ₹{fmt(locked_price)} &middot; Market ₹{fmt(market_price)}/kg
        </p>
        <p className="text-[10px] font-mono font-bold text-red-700">+₹{fmt(diff_per_kg)}/kg over</p>
      </div>
    );
  }
  if (status === 'breakeven') {
    return (
      <div className="text-right" data-testid="rm-status-breakeven">
        <span className="text-xs font-bold text-amber-600">BREAK-EVEN</span>
        <p className="text-[10px] font-mono text-amber-500 mt-0.5">₹{fmt(locked_price)}/kg = Market</p>
      </div>
    );
  }
  return (
    <div className="text-right" data-testid="rm-status-safe">
      <div className="flex items-center justify-end gap-1.5">
        <CheckCircle size={12} className="text-emerald-500" />
        <span className="text-xs font-bold text-emerald-600">SAFE</span>
      </div>
      <p className="text-[10px] font-mono text-emerald-600 mt-0.5">
        Locked ₹{fmt(locked_price)} &middot; Market ₹{fmt(market_price)}/kg
      </p>
      <p className="text-[10px] font-mono font-bold text-emerald-700">₹{fmt(-diff_per_kg)}/kg buffer</p>
    </div>
  );
}

export function MarginBadge({ pct, threshold = 15 }) {
  const p = Number(pct) || 0;
  if (p >= threshold) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">{p.toFixed(1)}%</span>;
  if (p >= 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 font-mono">{p.toFixed(1)}%</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 font-mono">{p.toFixed(1)}%</span>;
}

// Balloon shown next to a plating price: best supplier plating cost, or a
// warning when no plating supplier has been allotted yet. `info` is the part's
// entry from GET /plating-costings/summary (undefined = no supplier set).
export function PlatingSupplierBubble({ info, compact = false, customerRate = null }) {
  if (info && info.best_plating_cost != null) {
    const extra = info.supplier_count > 1 ? ` +${info.supplier_count - 1}` : '';
    // The customer plating rate must be GREATER than what we pay the plater. If it isn't
    // (customer ≤ supplier), we lose money on plating → show the bubble in red.
    const loss = customerRate != null && Number(customerRate) <= info.best_plating_cost;
    const cls = loss
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
    const title = `Best plating supplier: ${info.best_supplier_name} — ${fmtCur(info.best_plating_cost)}`
      + (info.supplier_count > 1 ? ` (${info.supplier_count} suppliers priced)` : '')
      + (loss ? ` — customer plating rate ${fmtCur(customerRate)} ≤ supplier rate: losing on plating` : '');
    return (
      <span title={title} data-testid="plating-supplier-bubble"
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap max-w-full ${cls}`}>
        <Truck size={10} className="shrink-0" />
        <span className="font-mono">{fmtCur(info.best_plating_cost)}</span>
        {!compact && <span className="truncate max-w-[90px]">{info.best_supplier_name}{extra}</span>}
      </span>
    );
  }
  return (
    <span title="No supplier plating cost set — allot a plating supplier in the Plating section"
      data-testid="plating-supplier-missing"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
      <AlertTriangle size={10} className="shrink-0" /> No supplier
    </span>
  );
}

// Sum process costs for a part. Returns 0 if no processes.
/** A part is "negative" (needs attention / a note) when it's losing money or priced under cost:
 *  negative margin %, an over-PO reconciliation (unallocated/contribution below zero), or the
 *  add-on (Sell − direct costs) below zero. */
export function isNegativePart(part) {
  if (!part) return false;
  const margin = Number(part.margin_pct);
  const recon = Number(part.unallocated_balance ?? part.contribution);
  const addOnV = Number(part.add_on);
  return (Number.isFinite(margin) && margin < 0)
    || (Number.isFinite(recon) && recon < -0.01)
    || (Number.isFinite(addOnV) && addOnV < 0);
}

export const procTotal = (part) =>
  (part?.processes || []).reduce((s, p) => s + (Number(p?.cost) || 0), 0);

// Sum BOP item costs for a part. Returns 0 if no BOP items.
export const bopTotal = (part) =>
  (part?.bop_items || []).reduce((s, b) => s + (Number(b?.cost) || 0), 0);

// Net RMC: prefer backend-calculated rm_cost_net, fall back to recompute.
export const netRmc = (part) => {
  if (part == null) return 0;
  if (part.rm_cost_net != null) return Number(part.rm_cost_net) || 0;
  const ec = part.extracted_costing || {};
  if (ec.net_rmc != null) return Number(ec.net_rmc) || 0;
  const gw = Number(part.gross_weight) || 0;
  const nw = Number(part.net_weight) || 0;
  const rm = Number(part.rm_price_per_kg) || 0;
  const sp = Number(part.scrap_price_per_kg) || 0;
  return gw * rm - Math.max(gw - nw, 0) * sp;
};

// Add-on = Sale price minus the four direct cost components. Can be negative.
export const addOn = (part) => {
  const sp = Number(part?.selling_price) || 0;
  return sp - netRmc(part) - procTotal(part) - (Number(part?.plating_cost) || 0) - bopTotal(part);
};

// Percentage of sale price. Returns null if sale price is 0/missing.
export const pctOfSale = (value, salePrice) => {
  const sp = Number(salePrice) || 0;
  if (sp === 0) return null;
  return (Number(value) || 0) / sp * 100;
};

// Render a value + %-of-sale below it.
export function CostCell({ value, salePrice, alwaysShow = false, emptyLabel = '—' }) {
  const v = Number(value) || 0;
  if (!alwaysShow && v === 0) {
    return <span className="text-slate-300">{emptyLabel}</span>;
  }
  const pct = pctOfSale(v, salePrice);
  const isNeg = v < 0;
  return (
    <div className={`leading-tight ${isNeg ? 'text-red-600' : 'text-slate-700'}`}>
      <div className="font-mono text-sm">{fmtCur(v)}</div>
      {pct != null && (
        <div className={`text-[10px] font-mono ${isNeg ? 'text-red-400' : 'text-slate-400'}`}>
          ({pct.toFixed(1)}%)
        </div>
      )}
    </div>
  );
}
